import { type Metadata } from 'next';
import { assoc, omit } from 'ramda';

import {
  type FullParams,
  type PageType,
  getPageUrl,
  internalToRouteMapping,
  isPageIndexable,
  splitUrlRegion,
} from '@/app/urls/url-mapping';
import { regionToMainCountry } from '@/lib/utils';
import type { Language, Region, RegionCountry, UrlRegion } from '@/types';

export type SeoMetadata = {
  seo: {
    description: string;
    title: string;
  } | null;
};

export type Alternates = Partial<
  Record<`${Language}-${RegionCountry}`, string>
>;

export const getAlternateLocale = (
  region: Region,
  language: Language
): keyof Alternates => `${language}-${regionToMainCountry(region)}`;

// Removes params that we don't want when creating alternate region URLs:
// - pagination because that page may not exist in the other region
// - slug because the slug might be different in the other region
export const sanitizeAlternateParams = omit(['pagination', 'slug']);

export const getAlternateMetaTags = (
  pageType: PageType,
  params: FullParams
): Pick<Metadata, 'alternates'> => {
  if (!isPageIndexable(pageType, params)) return {};

  const canonicalParams =
    params.region === 'ww' ? assoc('region', 'us', params) : params;
  let canonical = getPageUrl(pageType, canonicalParams, { withOrigin: true });

  const experimentIndexInUrl = canonical?.indexOf('/e/');
  if (experimentIndexInUrl !== -1) {
    canonical = canonical?.slice(0, experimentIndexInUrl);
  }

  const pageRoutes = internalToRouteMapping[pageType];

  if (!pageRoutes) return { alternates: { canonical } };

  const paramsWithoutPagination = sanitizeAlternateParams(params);
  const pageRegions = Object.keys(pageRoutes) as UrlRegion[];

  const languages = pageRegions.reduce<Alternates>((regionAcc, urlRegion) => {
    const { region, language } = splitUrlRegion(urlRegion);

    const url = getPageUrl(
      pageType,
      { ...paramsWithoutPagination, language, region },
      { withOrigin: true }
    );

    if (
      !url ||
      !isPageIndexable(pageType, { language, region }) ||
      region === 'ww'
    )
      return regionAcc;

    const locale = getAlternateLocale(region, language);
    return { ...regionAcc, [locale]: url };
  }, {});

  return { alternates: { canonical, languages } };
};

export const getRobotsMetaTags = (
  pageType: PageType,
  params: FullParams
): Pick<Metadata, 'robots'> =>
  isPageIndexable(pageType, params)
    ? {}
    : {
        robots: {
          index: false,
          ...(params.region === 'ww' && { follow: true }),
        },
      };
